import { BKJButton, BKJIcon, BKJLoadingSpinner, Flex } from '@bookjane2/bookjane-design-library';
import { useShiftModalDataContext } from 'modals/ShiftModal/ShiftModalProvider.data.context';
import {
  CallOutHeader,
  CallOutWrapper,
  CallOutViewDetailsLink,
  CallOutShiftDistributionLink,
  CallOutTitleSection,
  CallOutBodySection,
  EstimatedTimeSection,
  ProgressStatusSection,
  BKJProgressBar,
  CallOutProgressBarSection,
  CallOutHeaderText,
  CallOutHeaderIcon,
  CallOutHeaderWrapper,
  ProgressBold,
  ProgressText,
  Description,
  CallOutBeingFinalized,
} from 'modals/ShiftModal/src/views/DetailView/GeneralTab/CallOut.styled';
import { WarningIcon } from 'pages/Schedule/modals/SchedulePublishModal/styled';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'hooks/useTranslation';
import { shiftModalRoutingService } from 'modals/ShiftModal/ShiftModal.ui.context';
import { OrderStatusEnum } from 'types/graphql';
import { ContinueCalloutDialog } from 'components/ContinueCalloutDialog';
import { queryRefetchService } from 'services/QueryRefetchService';
import { toastService } from 'services';
import { DateTime } from 'luxon';
import { useSelector } from 'react-redux';
import { isShiftBiddingFeatureEnabledSelector } from 'store/selectors/featureFlagSelectors';

export const calculateEstimatedTimeRemaining = (
  estimatedCompletedAt: string,
  timeZone: string | undefined,
) => {
  const now = DateTime.now().setZone(timeZone);
  const estimatedCalloutCompleteTimestamp =
    DateTime.fromISO(estimatedCompletedAt).setZone(timeZone);

  const totalMins = Math.floor(
    estimatedCalloutCompleteTimestamp.diff(now, 'minutes').as('minutes'),
  );
  const mins = Math.floor(totalMins % 60);

  const totalHours = Math.floor(estimatedCalloutCompleteTimestamp.diff(now, 'hours').as('hours'));
  const hours = Math.floor(totalHours % 24);

  const totalDays = Math.floor(estimatedCalloutCompleteTimestamp.diff(now, 'days').as('days'));
  const days = Math.floor(totalDays);

  return { mins, hours, days };
};

export function CallOutSection() {
  const { t } = useTranslation();
  const { data, refetch } = useShiftModalDataContext();
  const order = data!.order;

  const timeZone = order.timeZone?.toString();

  const isCompleted = useMemo(
    () =>
      order.currentlyActiveCalloutList?.isCompleted &&
      order.status.value === OrderStatusEnum.pending,
    [order],
  );

  const [isContinueCallOutConfirmationOpen, setIsContinueCallOutConfirmationOpen] = useState(false);
  const isShiftBiddingFeatureEnabled = useSelector(isShiftBiddingFeatureEnabledSelector);
  const isCalloutPaused = useMemo(
    () =>
      order.currentlyActiveCalloutList?.waitingForManualApproval &&
      order.status.value === OrderStatusEnum.pending &&
      !(isShiftBiddingFeatureEnabled && order.biddable) &&
      !isCompleted,
    [
      order.biddable,
      order.currentlyActiveCalloutList?.waitingForManualApproval,
      order.status.value,
      isCompleted,
      isShiftBiddingFeatureEnabled,
    ],
  );

  const goToAuditTrailView = useCallback(
    (calloutListId: string) =>
      shiftModalRoutingService.goToView('AuditTrailView', {
        calloutListId: calloutListId,
      }),
    [],
  );

  const goToSettingsShiftDistributionView = useCallback(() => {
    window.history.pushState(
      {},
      'BookJane',
      `/settings/locations/${order.community.id}/shift-distribution`,
    );
    window.location.reload();
  }, [order.community.id]);

  if (!order.currentlyActiveCalloutList) {
    return <></>;
  }

  const totalProgress = order.currentlyActiveCalloutList.totalJanes;

  const { mins, hours, days } = calculateEstimatedTimeRemaining(
    order.currentlyActiveCalloutList.estimatedCompletedAt,
    timeZone,
  );

  const nextCalloutGroup =
    !!isCalloutPaused &&
    order.currentlyActiveCalloutList &&
    order.currentlyActiveCalloutList.manualApprovalIndexes?.length
      ? order.currentlyActiveCalloutList.manualApprovalIndexes[0].groupName
      : '';

  return (
    <CallOutWrapper>
      <CallOutTitleSection>
        <Flex flexDirection="row" flex="1">
          <CallOutHeader>
            <CallOutHeaderWrapper>
              <CallOutHeaderText>
                {t('shift_modal:CALL_OUT')}{' '}
                {isCompleted
                  ? t('shift_modal:COMPLETE')
                  : isCalloutPaused
                  ? t('shift_modal:PAUSED')
                  : t('shift_modal:IN_PROGRESS')}
                &nbsp;
              </CallOutHeaderText>
              {isCalloutPaused && <BKJIcon iconName="Pause" />}
              <CallOutHeaderIcon>{isCompleted && <WarningIcon />}</CallOutHeaderIcon>
            </CallOutHeaderWrapper>
          </CallOutHeader>
          <CallOutViewDetailsLink
            onClick={() => goToAuditTrailView(order.currentlyActiveCalloutList!.id)}
          >
            {t('shift_modal:VIEW_DETAILS')}
          </CallOutViewDetailsLink>
        </Flex>
      </CallOutTitleSection>
      <CallOutProgressBarSection>
        <BKJProgressBar
          value={order.currentlyActiveCalloutList.totalNotifiedAndSkipped}
          max={totalProgress}
          height="8px;"
        ></BKJProgressBar>
      </CallOutProgressBarSection>
      <CallOutBodySection>
        <Flex flexDirection="row" flex="1">
          <ProgressStatusSection>
            <ProgressBold>{t('shift_modal:PROGRESS')}:&nbsp;</ProgressBold>
            <ProgressText>
              {order.currentlyActiveCalloutList.totalNotifiedAndSkipped}/{totalProgress}
            </ProgressText>
          </ProgressStatusSection>
          <EstimatedTimeSection>
            <ProgressBold>{t('shift_modal:EST_TIME_REMAINING')}:&nbsp;</ProgressBold>
            <ProgressText>
              {days > 0 && <span>{days}d&nbsp;</span>}
              {hours > 0 && <span>{hours}hr&nbsp;</span>}
              {mins > 0 && <span>{mins}m&nbsp;</span>}
              {days <= 0 && hours <= 0 && mins <= 0 && <span>-</span>}
            </ProgressText>
          </EstimatedTimeSection>
        </Flex>
        <Flex>
          <Description>
            {t('shift_modal:EST_TIME_REMAINING_BASED_ON')}{' '}
            <CallOutShiftDistributionLink onClick={goToSettingsShiftDistributionView}>
              {t('shift_modal:SHIFT_DISTRIBUTION_RULES')}
            </CallOutShiftDistributionLink>{' '}
            {t('shift_modal:FOR_THIS_LOCATION')} {t('shift_modal:REFRESH_TO_VIEW_UPDATES')}
          </Description>
        </Flex>
        <Flex justifyContent="space-between">
          {nextCalloutGroup && (
            <Flex padding="20px 0 0 0" flexDirection="row">
              {t('shift_modal:NEXT_GROUP')}:&nbsp;{nextCalloutGroup}
            </Flex>
          )}
          {isCalloutPaused && (
            <Flex flexDirection="row-reverse" padding="10px 0 0 0">
              <BKJButton
                variant="PurpleSolid"
                width="192px"
                onClick={(e) => setIsContinueCallOutConfirmationOpen(true)}
              >
                {t('shift_modal:CONTINUE_CALL_OUT')}
              </BKJButton>
            </Flex>
          )}
        </Flex>
        {!!isCalloutPaused && (
          <ContinueCalloutDialog
            isContinueCallOutConfirmationOpen={isContinueCallOutConfirmationOpen}
            setIsContinueCallOutConfirmationOpen={setIsContinueCallOutConfirmationOpen}
            orderRelayId={order.relayId}
            onSuccess={() => {
              toastService.success(t('shift_modal:CALL_OUT_SUCCESSFUL_RESUMED'));
              queryRefetchService.refetchDataQueryByKey('SHIFT_CARD', `${order.relayId}`);
              if (refetch) refetch();
            }}
          />
        )}
      </CallOutBodySection>
    </CallOutWrapper>
  );
}

export const CalloutNotFinalizedView = () => {
  const { t } = useTranslation();
  return (
    <CallOutWrapper>
      <Flex flexDirection="column" padding="0 0 25px 0" justifyContent="center" alignItems="center">
        <CallOutBeingFinalized>
          {t('shift_modal:CALLOUT_CREATION_IN_PROGRESS')}
        </CallOutBeingFinalized>
        <BKJLoadingSpinner variant="Small" />
      </Flex>
    </CallOutWrapper>
  );
};
